import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { of, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { OpportunityBackend } from '@libs/src/opportunity/opportunity.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import {
  getOpportunityErrorMessages,
  isOpportunityTypeEvent,
} from '@main-client/src/app/opportunity/opportunity.helper';
import { Opportunity } from '@main-client/src/app/opportunity/opportunity.model';

import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';

@Injectable()
export class GroupsLegacyGuardService {
  constructor(
    public opportunityBackend: OpportunityBackend,
    public router: Router,
    public toastService: ToastService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot) {
    const groupSlug = get(route, 'params.groupSlug');
    const opportunitySlug = get(
      route,
      'queryParams.opportunitySlug',
      get(route, 'params.opportunitySlug'),
    );
    const path = get(route, 'url[0].path');
    const getOpportunityObservable = this.opportunityBackend
      .getOpportunity(opportunitySlug)
      .pipe(
        map((response: { data: Opportunity }) => response.data),
        catchError((response: any) => {
          const errorMessage = get(
            getOpportunityErrorMessages,
            response.status,
            get(response, 'error.message', response.message),
          );
          this.toastService.showError(errorMessage);
          return throwError(errorMessage);
        }),
      );
    const opportunityObservable = isEqual(path, opportunitySlug)
      ? getOpportunityObservable
      : of({});
    return opportunityObservable.pipe(
      take(1),
      map((opportunity: Opportunity) => {
        const queryParamOpportunityType = get(route, 'queryParams.type');
        const resolvedOpportunityType = isOpportunityTypeEvent(opportunity)
          ? 'event'
          : 'article';
        const opportunityType =
          queryParamOpportunityType || resolvedOpportunityType;
        if (!path) {
          return this.router.createUrlTree(['/home']);
        }
        switch (path) {
          case 'group-chat-manager': {
            return this.router.createUrlTree(['hub', groupSlug, 'events']);
          }
          case 'create-opportunity': {
            return isEqual('content', opportunityType)
              ? this.router.createUrlTree([
                  'hub',
                  groupSlug,
                  'articles',
                  'create',
                ])
              : this.router.createUrlTree([
                  'hub',
                  groupSlug,
                  'events',
                  'create',
                ]);
          }
          case 'edit-opportunity': {
            return isEqual('content', opportunityType)
              ? this.router.createUrlTree([
                  'hub',
                  groupSlug,
                  'article',
                  opportunitySlug,
                  'edit',
                ])
              : this.router.createUrlTree([
                  'hub',
                  groupSlug,
                  'event',
                  opportunitySlug,
                  'edit',
                ]);
          }
          case 'content-manager': {
            return this.router.createUrlTree(['hub', groupSlug, 'articles']);
          }
          case opportunitySlug: {
            return this.router.createUrlTree([
              'hub',
              groupSlug,
              opportunityType,
              opportunitySlug,
            ]);
          }
          default: {
            return true;
          }
        }
      }),
      catchError(() => {
        const notFoundUrlTree = this.router.createUrlTree(['/not-found']);
        return of(notFoundUrlTree);
      }),
    );
  }
}
